import { USAMap } from '@mirawision/usa-map-react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { formatMoney } from 'utils/utils';
import { COLORS } from './CommissionsGraph';

const BUCKETS = [
  { label: '$500K+', min: 500_000, color: '#0d3d6b' },
  { label: '$200K–$499K', min: 200_000, color: '#1565a8' },
  { label: '$50K–$199K', min: 50_000, color: '#2670B7' },
  { label: '$20K–$49K', min: 20_000, color: '#5b9fd4' },
  { label: '$1K–$19K', min: 1_000, color: '#a8ccec' },
  { label: '$1–$1K', min: 1, color: '#c8dff2' },
  { label: '$0', min: 0, color: '#e8e8e8' }
];

function getBucketColor(amount) {
  for (const bucket of BUCKETS) {
    if (amount >= bucket.min) return bucket.color;
  }
  return BUCKETS[BUCKETS.length - 1].color;
}

function isCommissionEntry(val) {
  return typeof val === 'object' && val && 'commission' in val;
}

function getCommissionAmount(val) {
  return isCommissionEntry(val) ? val.commission : val;
}

function getStateTotal(stateData) {
  return Object.values(stateData).reduce(
    (acc, val) => acc + getCommissionAmount(val),
    0
  );
}

function CommissionsMap({ data_for_state_commissions }) {
  const sortedStates = useMemo(() => {
    return Object.entries(data_for_state_commissions)
      .filter(([stateCode]) => stateCode && stateCode !== 'null')
      .map(([stateCode, carriers]) => ({
        stateCode,
        total: getStateTotal(carriers)
      }))
      .sort((a, b) => b.total - a.total);
  }, [data_for_state_commissions]);

  const customStates = useMemo(() => {
    const result = {};
    Object.entries(data_for_state_commissions)
      .filter(([stateCode]) => stateCode && stateCode !== 'null')
      .forEach(([stateCode, carriers]) => {
        const total = getStateTotal(carriers);
        result[stateCode] = {
          fill: getBucketColor(total),
          label: { enabled: true, fill: '#ffffff' },
          tooltip: {
            enabled: true,
            render: () => (
              <div style={{ maxWidth: '350px', minWidth: '350px' }}>
                <strong>
                  {stateCode} — {formatMoney(total)}
                </strong>
                <br />
                <br />
                {Object.keys(carriers).map(carrier => {
                  const val = carriers[carrier];
                  const amount = getCommissionAmount(val);
                  return (
                    <div key={carrier}>
                      <strong>
                        <span style={{ color: COLORS[carrier] }}>●</span>{' '}
                        {carrier}
                      </strong>{' '}
                      <br />
                      {formatMoney(amount)}
                    </div>
                  );
                })}
              </div>
            )
          }
        };
      });
    return result;
  }, [data_for_state_commissions]);

  return (
    <div
      className='rounded-[14px] border border-[#d9d9da] w-full bg-white flex flex-col'
      style={{ padding: '16px' }}
    >
      <label className='font-semibold text-gray-700'>
        Commissions By: <span className='font-bold'>State</span>
      </label>

      {/* Bucket legend */}
      <div data-tour='commissions-map' className='flex flex-wrap gap-x-4 gap-y-1.5 mt-3'>
        {BUCKETS.map(bucket => (
          <div key={bucket.label} className='flex items-center gap-1.5'>
            <span
              className='inline-block w-4 h-4 rounded-sm shrink-0'
              style={{ backgroundColor: bucket.color }}
            />
            <span className='text-xs text-gray-600'>{bucket.label}</span>
          </div>
        ))}
      </div>

      {/* Map */}
      <style>{`
        .usa-map-tooltip {
          padding: 8px 12px !important;
          border-radius: 6px !important;
          font-size: 14px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
          pointer-events: none !important;
          z-index: 1000 !important;
          min-width: 300px !important;
        }
      `}</style>
      <div className='mt-3 w-full min-w-0'>
        <div
          className='relative w-full max-w-full [&_.usa-map]:block [&_.usa-map]:h-full [&_.usa-map]:w-full [&_svg]:box-border [&_svg]:block [&_svg]:h-full [&_svg]:w-full [&_svg]:max-h-full'
          style={{ aspectRatio: '959 / 593' }}
        >
          <USAMap
            mapSettings={{ width: '100%', height: '100%' }}
            customStates={customStates}
          />
        </div>
      </div>

      {/* Scrollable state list */}
      {sortedStates.length > 0 && (
        <div className='mt-4 border-t border-[#d9d9da] pt-3'>
          <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>
            Top States
          </span>
          <div
            className='mt-2 flex flex-col gap-1 overflow-y-scroll'
            style={{
              maxHeight: '200px',
              scrollbarWidth: 'thin',
              scrollbarColor: '#c0c0c0 #f0f0f0'
            }}
          >
            {sortedStates.map(({ stateCode, total }) => (
              <div
                key={stateCode}
                className='flex items-center justify-between px-2 py-1 rounded-md hover:bg-gray-50'
              >
                <div className='flex items-center gap-2'>
                  <span
                    className='inline-block w-2.5 h-2.5 rounded-sm shrink-0'
                    style={{ backgroundColor: getBucketColor(total) }}
                  />
                  <span className='text-sm text-gray-700 font-medium'>
                    {stateCode}
                  </span>
                </div>
                <span className='text-sm text-gray-800 font-semibold'>
                  {formatMoney(total)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

CommissionsMap.propTypes = {
  data_for_state_commissions: PropTypes.object.isRequired
};

export default CommissionsMap;
